import React, { useCallback, useEffect, useState } from "react";
import {
  Alert,
  AlertIcon,
  Badge,
  Box,
  Button,
  Checkbox,
  Circle,
  Container,
  Divider,
  Flex,
  FormControl,
  FormLabel,
  Heading,
  HStack,
  IconButton,
  Input,
  Select,
  SimpleGrid,
  Spinner,
  Switch,
  Table,
  Tag,
  TagCloseButton,
  TagLabel,
  Tbody,
  Td,
  Text,
  Textarea,
  Th,
  Tooltip,
  Thead,
  Tr,
  useToast,
  VStack,
} from "@chakra-ui/react";
import { DeleteIcon } from "@chakra-ui/icons";
import { useLocation, useNavigate, useParams } from "react-router-dom";

import { generateInviteLink } from "../api/invite";
import { inviteSubject } from "../utils/inviteSubject";
import { useAuth } from "../auth/AuthContext";

import PageHeader from "../components/PageHeader";
import {
  getPickemPayouts,
  getPickemPool,
  removePickemMember,
  savePickemPayouts,
  sendPickemInvites,
  sendPickemRecapTest,
  updatePickemMember,
  updatePickemPool,
} from "../api/nflPickem";
import { useNflSeason } from "../hooks/useNflSeason";
import PayoutTotalLine from "../components/PayoutTotalLine";
import { payoutTotalError } from "../utils/payoutTotals";

const TOP_BAR_BLUE = "#00005f";
const PAGE_BLUE = "#00007f";

export default function NflPickemPoolSetup() {
  const { poolId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const toast = useToast();
  const { user, logout } = useAuth();
  const { seasonId, loading: seasonLoading } = useNflSeason();
  const effectiveSeasonId = location.state?.seasonId ?? seasonId;

  const [pool, setPool] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState("");

  // Settings
  const [name, setName] = useState("");
  const [poolDepth, setPoolDepth] = useState("");
  const [isPrivate, setIsPrivate] = useState(false);
  const [pickAgainstLimit, setPickAgainstLimit] = useState(3);
  const [divisionLockWeek, setDivisionLockWeek] = useState(4);
  const [underdogEnabled, setUnderdogEnabled] = useState(false);
  const [underdogThreshold, setUnderdogThreshold] = useState(4.5);
  const [underdogUses, setUnderdogUses] = useState(1);
  const [weeklyRecap, setWeeklyRecap] = useState(true);
  const [pickReminders, setPickReminders] = useState(true);

  // Payouts
  const [payouts, setPayouts] = useState([]);
  const [savingPayouts, setSavingPayouts] = useState(false);
  const [payoutError, setPayoutError] = useState("");

  // Invites
  const [emailInput, setEmailInput] = useState("");
  const [emails, setEmails] = useState([]);
  const [inviteMessage, setInviteMessage] = useState("");
  const [sendingInvites, setSendingInvites] = useState(false);
  const [sendingRecap, setSendingRecap] = useState(false);
  const [sendMode, setSendMode] = useState("invite");
  const [inviteLink, setInviteLink] = useState("");
  const [linkLoading, setLinkLoading] = useState(false);

  const username = user?.username || user?.email || "A friend";

  function buildInviteMessage(poolName, inviterName, link, depth) {
    // Stated up front so an invitee knows the cost before joining.
    // Omitted entirely when the pool has no buy-in.
    const amount = Number(depth);
    const buyIn =
      depth && !Number.isNaN(amount) && amount > 0
        ? ` The buy-in for this pool is currently $${
            Number.isInteger(amount) ? amount : amount.toFixed(2)
          }.`
        : "";

    return (
      `Hi!\n\nYou've been invited to join "${poolName}" on The Deep End — an NFL ` +
      `Pick 'Em pool — by ${inviterName}.${buyIn}\n\n` +
      `Click the link below to join directly:\n${link}\n\n` +
      `Good luck!\n— ${inviterName}`
    );
  }

  const load = useCallback(async () => {
    if (!effectiveSeasonId || !poolId) return;
    setLoading(true);
    try {
      const [p, pay] = await Promise.all([
        getPickemPool(effectiveSeasonId, poolId),
        getPickemPayouts(effectiveSeasonId, poolId).catch(() => []),
      ]);
      setPool(p);
      setName(p.name || "");
      setPoolDepth(p.pool_depth ?? "");
      setIsPrivate(!!p.is_private);
      setPickAgainstLimit(p.pick_against_limit ?? 3);
      setDivisionLockWeek(p.division_lock_week ?? 4);
      setUnderdogEnabled(!!p.underdog_bonus_enabled);
      setUnderdogThreshold(p.underdog_threshold ?? 4.5);
      setUnderdogUses(p.underdog_uses ?? 1);
      setWeeklyRecap(!!p.weekly_recap_enabled);
      setPickReminders(!!p.pick_reminders_enabled);
      setPayouts(pay.length ? pay : [
        { label: "1st Place", amount: "" },
        { label: "2nd Place", amount: "" },
        { label: "3rd Place", amount: "" },
      ]);

      try {
        const { link } = await generateInviteLink({
          game: "nfl_pk", poolId: Number(poolId), seasonId: effectiveSeasonId,
        });
        setInviteLink(link);
        setInviteMessage(buildInviteMessage(p.name, username, link, p.pool_depth));
      } catch {
        // Invites can be closed (season started) — the Message tab still works.
        setInviteLink("");
      }
    } catch (err) {
      setSaveError(err?.response?.data?.detail || "Unable to load this pool.");
    } finally {
      setLoading(false);
    }
  }, [effectiveSeasonId, poolId, username]);

  useEffect(() => {
    if (!seasonLoading) load();
  }, [load, seasonLoading]);

  async function handleSave() {
    setSaving(true);
    setSaveError("");
    try {
      await updatePickemPool(effectiveSeasonId, poolId, {
        name: name.trim(),
        pool_depth: poolDepth === "" ? null : Number(poolDepth),
        is_private: isPrivate,
        pick_against_limit: Number(pickAgainstLimit),
        division_lock_week: Number(divisionLockWeek),
        underdog_bonus_enabled: underdogEnabled,
        underdog_threshold: Number(underdogThreshold),
        underdog_uses: Number(underdogUses),
        weekly_recap_enabled: weeklyRecap,
        pick_reminders_enabled: pickReminders,
      });
      toast({ title: "Settings saved.", status: "success", duration: 2000, isClosable: true });
      await load();
    } catch (err) {
      setSaveError(err?.response?.data?.detail || "Unable to save settings.");
    } finally {
      setSaving(false);
    }
  }

  async function handleSavePayouts() {
    setSavingPayouts(true);
    setPayoutError("");
    try {
      const cleaned = payouts
        .filter((p) => p.label?.trim() && p.amount !== "")
        .map((p, i) => ({
          label: p.label.trim(),
          amount: Number(p.amount),
          sort_order: i,
        }));

      // Allow under, block over — see utils/payoutTotals.js. The server
      // enforces the same rule; this is it said early.
      const overPot = payoutTotalError(cleaned.map((p) => p.amount), pool?.total_pot);
      if (overPot) {
        setPayoutError(overPot);
        return;
      }

      const saved = await savePickemPayouts(effectiveSeasonId, poolId, cleaned);
      setPayouts(saved.length ? saved : [
        { label: "1st Place", amount: "" },
        { label: "2nd Place", amount: "" },
        { label: "3rd Place", amount: "" },
      ]);
      toast({ title: "Payouts saved.", status: "success", duration: 2000, isClosable: true });
    } catch (err) {
      setPayoutError(err?.response?.data?.detail || "Unable to save payouts.");
    } finally {
      setSavingPayouts(false);
    }
  }

  async function handleTogglePaid(member, checked) {
    try {
      await updatePickemMember(effectiveSeasonId, poolId, member.user_id, {
        paid: checked,
      });
      await load();
    } catch (err) {
      setSaveError(err?.response?.data?.detail || "Unable to update member.");
    }
  }

  async function handleRemoveMember(member) {
    // Irreversible and one stray click away, so confirm by name first.
    const ok = window.confirm(
      `Remove ${member.username || member.email} from this pool? ` +
      `Their picks and predictions will no longer count.`
    );
    if (!ok) return;
    try {
      await removePickemMember(effectiveSeasonId, poolId, member.user_id);
      toast({ title: "Member removed.", status: "success", duration: 2000, isClosable: true });
      await load();
    } catch (err) {
      setSaveError(err?.response?.data?.detail || "Unable to remove that member.");
    }
  }

  async function handleToggleCoAdmin(member, checked) {
    try {
      await updatePickemMember(effectiveSeasonId, poolId, member.user_id, {
        is_co_admin: checked,
      });
      await load();
    } catch (err) {
      setSaveError(err?.response?.data?.detail || "Unable to update member.");
    }
  }

  function addEmail() {
    // Split on commas, semicolons and whitespace so a pasted list becomes
    // separate tags. Typing fires this per address via the key handler, but a
    // paste never does, and the whole string would otherwise be accepted as
    // one address because it happens to contain an "@".
    const parts = emailInput
      .split(/[\s,;]+/)
      .map((e) => e.trim().toLowerCase())
      .filter((e) => e && e.includes("@"));
    if (!parts.length) return;
    setEmails((prev) => {
      const next = [...prev];
      parts.forEach((e) => { if (!next.includes(e)) next.push(e); });
      return next;
    });
    setEmailInput("");
  }

  function handleEmailKeyDown(e) {
    // Comma as well as Enter, since pasted lists tend to be comma separated.
    if (e.key === "Enter" || e.key === ",") {
      e.preventDefault();
      addEmail();
    }
  }

  function removeEmail(addr) {
    setEmails((prev) => prev.filter((e) => e !== addr));
  }

  function addAllMembers() {
    const memberEmails = (pool?.members || [])
      .map((m) => m.email)
      .filter((e) => e && e.includes("@") && !emails.includes(e));
    if (memberEmails.length) setEmails((prev) => [...prev, ...memberEmails]);
  }

  async function handleCopyLink() {
    if (!inviteLink) return;
    await navigator.clipboard.writeText(inviteLink);
    toast({ title: "Link copied!", status: "success", duration: 2000, isClosable: true });
  }

  async function switchToInvite() {
    setSendMode("invite");
    if (inviteLink) {
      setInviteMessage(buildInviteMessage(pool?.name || "", username, inviteLink, pool?.pool_depth));
      return;
    }
    setLinkLoading(true);
    try {
      const { link } = await generateInviteLink({
        game: "nfl_pk", poolId: Number(poolId), seasonId: effectiveSeasonId,
      });
      setInviteLink(link);
      setInviteMessage(buildInviteMessage(pool?.name || "", username, link, pool?.pool_depth));
    } catch (err) {
      setSaveError(err?.response?.data?.detail || "Unable to generate an invite link.");
    } finally {
      setLinkLoading(false);
    }
  }

  function switchToMessage() {
    setSendMode("message");
    setInviteMessage("");
  }

  async function handleSendInvites() {
    if (!emails.length) {
      toast({ title: "Add at least one email.", status: "warning", duration: 3000, isClosable: true });
      return;
    }
    const isInvite = sendMode === "invite";
    // An invite still says something useful with an empty box; a message is
    // nothing but its body, so it has to have one.
    if (!isInvite && !inviteMessage.trim()) {
      toast({ title: "Message cannot be empty.", status: "warning", duration: 3000, isClosable: true });
      return;
    }
    setSendingInvites(true);
    try {
      const out = await sendPickemInvites(
        effectiveSeasonId, poolId, emails,
        inviteMessage || "Come join our pool!",
        inviteSubject(sendMode, pool?.name),
        sendMode,
      );
      toast({
        title: isInvite
          ? `Sent ${out.sent} invite${out.sent === 1 ? "" : "s"}.`
          : `Sent ${out.sent} message${out.sent === 1 ? "" : "s"}.`,
        description: out.failed?.length ? `Failed: ${out.failed.join(", ")}` : undefined,
        status: out.failed?.length ? "warning" : "success",
        duration: 4000,
        isClosable: true,
      });
      setEmails([]);
      // A sent message is done with; the invite blurb is kept so the next
      // batch of addresses can go out without retyping it.
      if (!isInvite) setInviteMessage("");
    } catch (err) {
      setSaveError(err?.response?.data?.detail
        || (sendMode === "invite" ? "Unable to send invites." : "Unable to send the message."));
    } finally {
      setSendingInvites(false);
    }
  }

  function handleLogout() {
    logout();
    navigate("/login", { replace: true });
  }

  if (loading) {
    return (
      <Box minH="100vh" bg={PAGE_BLUE} color="white">
        <PageHeader title="NFL PICK 'EM SETUP" onBack={() => navigate("/nfl-pickem")}
                    user={user} onLogout={handleLogout} />
        <Flex justify="center" align="center" minH="300px"><Spinner size="xl" /></Flex>
      </Box>
    );
  }

  return (
    <Box minH="100vh" bg={PAGE_BLUE} color="white" overflowX="hidden">
      <PageHeader
        title="NFL PICK 'EM SETUP"
        onBack={() => navigate("/nfl-pickem")}
        user={user}
        onLogout={handleLogout}
      />

      <Container maxW="container.xl" py={8}>
        <VStack align="stretch" spacing={6}>
          {saveError && (
            <Alert status="error" borderRadius="md"><AlertIcon />{saveError}</Alert>
          )}

          <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={6} alignItems="stretch">
            {/* ── Settings ── */}
            <Box bg="white" color="black" border={`3px solid ${TOP_BAR_BLUE}`}
                 borderRadius="lg" boxShadow="lg" p={6}>
              <VStack align="stretch" spacing={5}>
                <Heading size="md" color={TOP_BAR_BLUE}>Pool Settings</Heading>

                <Flex direction={{ base: "column", md: "row" }} gap={4}>
                  <FormControl flex="1.6">
                    <FormLabel fontSize="sm">Pool Name</FormLabel>
                    <Input value={name} onChange={(e) => setName(e.target.value)} />
                  </FormControl>
                  <FormControl flex="1">
                    <FormLabel fontSize="sm">Buy-in per player ($)</FormLabel>
                    <Input type="number" min={1} value={poolDepth} placeholder="Optional"
                           onChange={(e) => setPoolDepth(e.target.value)} />
                  </FormControl>
                </Flex>

                <Divider />

                <HStack justify="space-between">
                  <Box>
                    <FormLabel fontSize="sm" mb={0}>Private Pool</FormLabel>
                    <Text fontSize="xs" color="gray.500">Invite only — not listed as joinable</Text>
                  </Box>
                  <Switch isChecked={isPrivate} colorScheme="blue"
                          onChange={(e) => setIsPrivate(e.target.checked)} />
                </HStack>

                <HStack justify="space-between">
                  <Box>
                    <FormLabel fontSize="sm" mb={0}>Weekly Recap Emails</FormLabel>
                    <Text fontSize="xs" color="gray.500">Results summary after each week scores</Text>
                  </Box>
                  <Switch isChecked={weeklyRecap} colorScheme="blue"
                          onChange={(e) => setWeeklyRecap(e.target.checked)} />
                </HStack>

                <HStack justify="space-between">
                  <Box>
                    <FormLabel fontSize="sm" mb={0}>Pick Reminder Emails</FormLabel>
                    <Text fontSize="xs" color="gray.500">
                      Remind players who haven&apos;t picked before kickoff
                    </Text>
                  </Box>
                  <Switch isChecked={pickReminders} colorScheme="blue"
                          onChange={(e) => setPickReminders(e.target.checked)} />
                </HStack>

                <Divider />
                <Heading size="sm" color={TOP_BAR_BLUE}>Pick &apos;Em Rules</Heading>

                <Flex direction={{ base: "column", md: "row" }} gap={4}>
                  <FormControl flex="1">
                    <FormLabel fontSize="sm">Pick-against limit</FormLabel>
                    <Input type="number" min={1} max={17} value={pickAgainstLimit}
                           onChange={(e) => setPickAgainstLimit(e.target.value)} />
                    <Text fontSize="xs" color="gray.500" mt={1}>
                      Times player may pick against same team.
                    </Text>
                  </FormControl>
                  <FormControl flex="1">
                    <FormLabel fontSize="sm">Division predictions lock</FormLabel>
                    <Select value={divisionLockWeek}
                            onChange={(e) => setDivisionLockWeek(e.target.value)}>
                      {Array.from({ length: 18 }, (_, i) => i + 1).map((w) => (
                        <option key={w} value={w}>Before Week {w}</option>
                      ))}
                    </Select>
                  </FormControl>
                </Flex>

                <HStack justify="space-between" align="flex-start">
                  <Box>
                    <FormLabel fontSize="sm" mb={0}>Underdog Bonus</FormLabel>
                    <Text fontSize="xs" color="gray.500">
                      Doubles the confidence staked on an underdog — win or lose.
                    </Text>
                  </Box>
                  <Switch isChecked={underdogEnabled} colorScheme="blue"
                          onChange={(e) => setUnderdogEnabled(e.target.checked)} />
                </HStack>

                {underdogEnabled && (
                  <Flex direction={{ base: "column", md: "row" }} gap={4} pl={2}>
                    <FormControl flex="1">
                      <FormLabel fontSize="sm">Underdog threshold (points)</FormLabel>
                      <Input type="number" step="0.5" min={0.5} value={underdogThreshold}
                             onChange={(e) => setUnderdogThreshold(e.target.value)} />
                    </FormControl>
                    <FormControl flex="1">
                      <FormLabel fontSize="sm">Uses per season</FormLabel>
                      <Input type="number" min={0} max={18} value={underdogUses}
                             onChange={(e) => setUnderdogUses(e.target.value)} />
                    </FormControl>
                  </Flex>
                )}

                <Divider />

                <Flex w="100%" gap={2} flexWrap="wrap">
                  <Button flex="1" size={{ base: "sm", md: "md" }} bg={TOP_BAR_BLUE} color="white"
                          _hover={{ bg: "#00004a" }} isLoading={saving} onClick={handleSave}>
                    Save<Box as="span" display={{ base: "none", sm: "inline" }}> Changes</Box>
                  </Button>
                  <Button flex="1" size={{ base: "sm", md: "md" }} variant="outline"
                          borderColor={TOP_BAR_BLUE} color={TOP_BAR_BLUE}
                          onClick={() => navigate(`/nfl-pickem/current/${poolId}`,
                                                  { state: { seasonId: effectiveSeasonId } })}>
                    Open Pool
                  </Button>
                  <Button flex="1" size={{ base: "sm", md: "md" }} variant="outline"
                          borderColor="orange.400" color="orange.500"
                          _hover={{ bg: "orange.50" }} isLoading={sendingRecap}
                          onClick={async () => {
                            setSendingRecap(true);
                            try {
                              await sendPickemRecapTest(effectiveSeasonId, poolId);
                              toast({
                                title: "Test recap sent to your email.",
                                status: "success", duration: 3000, isClosable: true,
                              });
                            } catch (err) {
                              toast({
                                title: err?.response?.data?.detail || "Failed to send test recap.",
                                status: "error", duration: 4000, isClosable: true,
                              });
                            } finally {
                              setSendingRecap(false);
                            }
                          }}>
                    <Box as="span" display={{ base: "none", sm: "inline" }}>Send </Box>Test Recap
                  </Button>
                </Flex>
              </VStack>
            </Box>

            {/* ── Payouts ── */}
            <Box bg="white" color="black" border={`3px solid ${TOP_BAR_BLUE}`}
                 borderRadius="lg" boxShadow="lg" p={6}>
              <VStack align="stretch" spacing={5}>
                <HStack justify="space-between" align="baseline">
                  <Heading size="md" color={TOP_BAR_BLUE}>Payouts</Heading>
                  {pool?.total_pot != null && (
                    <Text fontSize="sm" color="gray.600" fontWeight="semibold">
                      Total Pot:{" "}
                      <Text as="span" color={TOP_BAR_BLUE} fontWeight="bold">
                        ${Number(pool.total_pot).toFixed(2)}
                      </Text>
                    </Text>
                  )}
                </HStack>

                {payoutError && (
                  <Alert status="error" borderRadius="md" fontSize="sm">
                    <AlertIcon />{payoutError}
                  </Alert>
                )}

                <VStack align="stretch" spacing={2}>
                  {payouts.map((row, i) => (
                    <HStack key={i} spacing={3}>
                      <Input flex="1" size="sm" placeholder="Label (e.g. 1st Place)"
                             value={row.label || ""}
                             onChange={(e) => {
                               const next = [...payouts];
                               next[i] = { ...next[i], label: e.target.value };
                               setPayouts(next);
                             }} />
                      <Input w="100px" size="sm" type="number" min={0} placeholder="$0"
                             value={row.amount ?? ""}
                             onChange={(e) => {
                               const next = [...payouts];
                               next[i] = { ...next[i], amount: e.target.value };
                               setPayouts(next);
                             }} />
                      <IconButton icon={<DeleteIcon />} size="sm" variant="ghost"
                                  colorScheme="red" aria-label="Remove row"
                                  onClick={() => setPayouts(payouts.filter((_, j) => j !== i))} />
                    </HStack>
                  ))}
                </VStack>

                {/* Running total against the pot, so it's obvious what's left
                    before hitting save and being refused. */}
                {payouts.length > 0 && (
                  <PayoutTotalLine
                    amounts={payouts.map((r) => r.amount)}
                    pot={pool?.total_pot}
                  />
                )}

                <HStack>
                  <Button size="sm" variant="outline" borderColor={TOP_BAR_BLUE}
                          color={TOP_BAR_BLUE}
                          onClick={() => setPayouts([...payouts, { label: "", amount: "" }])}>
                    + Add Row
                  </Button>
                  <Button size="sm" bg={TOP_BAR_BLUE} color="white" _hover={{ bg: "#00004a" }}
                          isLoading={savingPayouts} onClick={handleSavePayouts}>
                    Save Payouts
                  </Button>
                </HStack>
              </VStack>
            </Box>
          </SimpleGrid>

          {/* ── Members ── */}
          <Box bg="white" color="black" border={`3px solid ${TOP_BAR_BLUE}`}
               borderRadius="lg" boxShadow="lg" p={6}>
            <VStack align="stretch" spacing={4}>
              <HStack justify="space-between" flexWrap="wrap" gap={2}>
                <Heading size="md" color={TOP_BAR_BLUE}>Members</Heading>
                {(() => {
                  const members = pool?.members || [];
                  const paid = members.filter((m) => m.paid).length;
                  const depth = pool?.pool_depth;
                  return (
                    <HStack spacing={3} flexWrap="wrap">
                      <Text fontSize="sm" color="gray.500">{members.length} joined</Text>
                      <Badge fontSize="sm" px={2}
                             colorScheme={paid === members.length && members.length ? "green" : "orange"}>
                        {paid} / {members.length} paid
                      </Badge>
                      {depth && (
                        <Text fontSize="sm" color="gray.500">
                          ${paid * depth} / ${members.length * depth} collected
                        </Text>
                      )}
                    </HStack>
                  );
                })()}
              </HStack>

              {(pool?.members || []).length > 0 ? (
                <Box overflowX="auto" overflowY="auto" maxH="400px">
                  <Table size="sm" variant="simple">
                    <Thead position="sticky" top={0} bg="white" zIndex={1}>
                      <Tr>
                        <Th>Member</Th>
                        <Th>Email</Th>
                        <Th textAlign="center">Paid</Th>
                        <Th textAlign="center">Co-Admin</Th>
                        <Th />
                      </Tr>
                    </Thead>
                    <Tbody>
                      {pool.members.map((m) => (
                        <Tr key={m.user_id}>
                          <Td>
                            <HStack spacing={2}>
                              <Circle size="28px" bg={TOP_BAR_BLUE} color="white"
                                      fontWeight="bold" fontSize="xs" flexShrink={0}>
                                {(m.username || "?").charAt(0).toUpperCase()}
                              </Circle>
                              <Text fontWeight="semibold" fontSize="sm">
                                {m.username || "—"}
                              </Text>
                              {m.user_id === pool.owner_user_id && (
                                <Badge colorScheme="blue" fontSize="2xs">Admin</Badge>
                              )}
                            </HStack>
                          </Td>
                          <Td>
                            <Text fontSize="sm" color="gray.600" noOfLines={1}>
                              {m.email || "—"}
                            </Text>
                          </Td>
                          <Td textAlign="center">
                            <Checkbox isChecked={!!m.paid} colorScheme="green"
                                      onChange={(e) => handleTogglePaid(m, e.target.checked)} />
                          </Td>
                          <Td textAlign="center">
                            {m.user_id !== pool.owner_user_id && (
                              <Checkbox isChecked={!!m.is_co_admin} colorScheme="blue"
                                        onChange={(e) => handleToggleCoAdmin(m, e.target.checked)} />
                            )}
                          </Td>
                          <Td textAlign="right" px={1}>
                            {/* The owner can't be removed — the API rejects it too. */}
                            {m.user_id !== pool.owner_user_id && (
                              <Tooltip label="Remove member" hasArrow>
                                <IconButton
                                  icon={<DeleteIcon />}
                                  size="xs"
                                  variant="ghost"
                                  colorScheme="red"
                                  aria-label="Remove member"
                                  onClick={() => handleRemoveMember(m)}
                                />
                              </Tooltip>
                            )}
                          </Td>
                        </Tr>
                      ))}
                    </Tbody>
                  </Table>
                </Box>
              ) : (
                <Text fontSize="sm" color="gray.500">No members yet.</Text>
              )}
            </VStack>
          </Box>

          {/* ── Invite / Message Players ── */}
          <Box bg="white" color="black" border={`3px solid ${TOP_BAR_BLUE}`}
               borderRadius="lg" boxShadow="lg" p={6}>
            <VStack align="stretch" spacing={4}>
              <HStack justify="space-between" align="center">
                <Heading size="md" color={TOP_BAR_BLUE}>Invite/Message Players</Heading>
                <Button size="xs" minH="44px" variant="outline" borderColor={TOP_BAR_BLUE}
                        color={TOP_BAR_BLUE} _hover={{ bg: "gray.50" }}
                        onClick={addAllMembers}>
                  Add all members
                </Button>
              </HStack>

              {/* Invite carries a join link and a canned message; Message is a
                  plain note to people already in the pool. */}
              <HStack spacing={0} borderRadius="md" overflow="hidden"
                      border={`1.5px solid ${TOP_BAR_BLUE}`} alignSelf="flex-start">
                <Button size="sm" minH="44px" borderRadius="0" px={5}
                        bg={sendMode === "invite" ? TOP_BAR_BLUE : "white"}
                        color={sendMode === "invite" ? "white" : TOP_BAR_BLUE}
                        _hover={{ bg: sendMode === "invite" ? TOP_BAR_BLUE : "gray.50" }}
                        isLoading={sendMode === "invite" && linkLoading}
                        onClick={switchToInvite}>
                  Invite
                </Button>
                <Button size="sm" minH="44px" borderRadius="0" px={5}
                        bg={sendMode === "message" ? TOP_BAR_BLUE : "white"}
                        color={sendMode === "message" ? "white" : TOP_BAR_BLUE}
                        _hover={{ bg: sendMode === "message" ? TOP_BAR_BLUE : "gray.50" }}
                        onClick={switchToMessage}>
                  Message
                </Button>
              </HStack>

              {sendMode === "invite" && inviteLink && (
                <HStack bg="gray.50" px={3} py={2} borderRadius="md"
                        border="1px solid #e2e8f0">
                  <Text fontSize="xs" color="gray.600" flex="1" isTruncated>
                    {inviteLink}
                  </Text>
                  <Button size="xs" minH="44px" variant="outline" flexShrink={0}
                          borderColor={TOP_BAR_BLUE} color={TOP_BAR_BLUE}
                          onClick={handleCopyLink}>
                    Copy Link
                  </Button>
                </HStack>
              )}

              <FormControl>
                <FormLabel fontSize="sm">Email Addresses</FormLabel>
                <HStack>
                  <Input size="sm" value={emailInput}
                         placeholder="Enter email and press Enter"
                         onChange={(e) => setEmailInput(e.target.value)}
                         onKeyDown={handleEmailKeyDown} />
                  <Button size="sm" minH="44px" flexShrink={0} bg={TOP_BAR_BLUE}
                          color="white" _hover={{ bg: "#00004a" }} onClick={addEmail}>
                    Add
                  </Button>
                </HStack>
                {emails.length > 0 && (
                  <Flex mt={2} flexWrap="wrap" gap={2}>
                    {emails.map((addr) => (
                      <Tag key={addr} size="sm" borderRadius="full" variant="solid"
                           colorScheme="blue">
                        <TagLabel>{addr}</TagLabel>
                        <TagCloseButton onClick={() => removeEmail(addr)} />
                      </Tag>
                    ))}
                  </Flex>
                )}
              </FormControl>
              <FormControl>
                <FormLabel fontSize="sm">
                  {sendMode === "invite" ? "Invite message" : "Message"}
                </FormLabel>
                <Textarea rows={6} value={inviteMessage}
                          placeholder={sendMode === "invite"
                            ? "Your invite message"
                            : "Write a note to the pool"}
                          onChange={(e) => setInviteMessage(e.target.value)} />
              </FormControl>
              <Button alignSelf="flex-start" bg={TOP_BAR_BLUE} color="white"
                      _hover={{ bg: "#00004a" }} isLoading={sendingInvites}
                      onClick={handleSendInvites}>
                {sendMode === "invite"
                  ? `Send Invite${emails.length > 1 ? "s" : ""}`
                  : `Send Message${emails.length > 1 ? "s" : ""}`}
              </Button>
            </VStack>
          </Box>
        </VStack>
      </Container>
    </Box>
  );
}
